<template>
  <div class="subjects-page">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 页面标题 -->
      <div class="text-center mb-12">
        <h1 class="text-3xl font-bold mb-3">按学科浏览</h1>
        <p class="text-neutral-500 max-w-2xl mx-auto">
          探索不同学科领域的AI工具，辅助您的学习与研究
        </p>
      </div>

      <!-- 学科列表网格 -->
      <div
        v-if="!selectedSubject"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-16"
      >
        <div
          v-for="subject in subjects"
          :key="subject.id"
          class="bg-white rounded-lg border border-neutral-200 p-6 text-center hover:border-primary hover:shadow-md transition-all duration-300 cursor-pointer group"
          @click="selectSubject(subject)"
        >
          <h3
            class="font-bold text-lg mb-2 group-hover:text-primary transition-colors"
          >
            {{ subject.title }}
          </h3>
          <p class="text-neutral-500 text-sm">
            {{ subject.toolCount || 0 }} 个工具
          </p>
        </div>
      </div>

      <!-- 工具列表区域 -->
      <div v-else id="subject-tools" class="animate-fade-in">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center">
            <button
              @click="clearSubject"
              class="mr-4 text-neutral-500 hover:text-primary transition-colors"
            >
              <i class="fa fa-arrow-left"></i>
            </button>
            <h2 class="text-2xl font-bold">
              {{ selectedSubject.title }} 相关工具
            </h2>
          </div>
        </div>

        <div v-if="loading" class="text-center py-10">
          <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
        </div>

        <div
          v-else-if="subjectTools.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          <div
            v-for="tool in subjectTools"
            :key="tool.id"
            class="bg-white rounded-xl border border-neutral-200 overflow-hidden hover-lift transition-all duration-300 cursor-pointer"
            @click="openToolDetail(tool)"
          >
            <div class="h-48 overflow-hidden bg-neutral-100 relative group">
              <img
                :src="tool.cover"
                :alt="tool.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div
                class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300"
              ></div>
              <div class="absolute top-3 right-3 flex">
                <div
                  class="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-neutral-600"
                >
                  {{ selectedSubject.title }}
                </div>
              </div>
            </div>
            <div class="p-5">
              <div class="flex justify-between items-start mb-2">
                <h3 class="font-bold text-lg truncate pr-2">
                  {{ tool.title }}
                </h3>
                <div
                  class="flex items-center text-xs text-yellow-500 font-medium bg-yellow-50 px-2 py-1 rounded"
                >
                  <i class="fa fa-star mr-1"></i>{{ tool.rating }}
                </div>
              </div>
              <p class="text-neutral-500 text-sm mb-4 line-clamp-2 h-10">
                {{ tool.description }}
              </p>
              <div
                class="flex items-center justify-between pt-4 border-t border-neutral-100"
              >
                <div class="text-xs text-neutral-400">
                  <i class="fa fa-download mr-1"></i>
                  {{ formatNumber(tool.downloadCount) }}
                </div>
                <button
                  class="text-primary hover:text-primary/80 text-sm font-medium"
                >
                  查看详情 <i class="fa fa-chevron-right text-xs ml-1"></i>
                </button>
              </div>
            </div>
          </div>

          <div class="flex justify-end w-full col-span-1 md:col-span-3">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="total"
              @current-change="pageChange"
            />
          </div>
        </div>

        <div v-else class="text-center py-16">
          <div class="inline-block p-4 rounded-full bg-neutral-100 mb-4">
            <i class="fa fa-book text-neutral-400 text-2xl"></i>
          </div>
          <h3 class="text-lg font-medium text-neutral-900 mb-1">暂无工具</h3>
          <p class="text-neutral-500">该学科下暂时没有工具，敬请期待。</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { globalState } from "@/state";
export default {
  name: "SubjectsPage",
  data() {
    return {
      subjects: [],
      selectedSubject: null,
      loading: false,
      subjectTools: [],
      page: 1,
      pageSize: 10,
      total: 0,
      categories: [], // For modal
    };
  },
  created() {
    this.getSubjects();
    // Get categories for modal
    this.$axios({
      url: "category/list/count",
    }).then((res) => {
      this.categories = res.data;
    });
  },
  watch: {
    "$route.query.subject": {
      handler() {
        this.checkQueryParam();
      },
    },
  },
  methods: {
    getSubjects() {
      this.$axios({
        url: "subinfo/list/count",
      }).then((res) => {
        this.subjects = res.data;
        this.checkQueryParam();
      });
    },
    openToolDetail(tool) {
      globalState.openModal(tool.id, this.categories);
    },
    async checkQueryParam() {
      const subjectId = this.$route.query.subject;
      if (subjectId) {
        // Wait for subjects to be loaded if not yet
        if (this.subjects.length === 0) return;

        const subject = this.subjects.find((s) => s.id == subjectId);
        if (subject) {
          await this.selectSubject(subject, false);
        }
      } else {
        this.selectedSubject = null;
      }
    },
    async pageChange(page) {
      this.page = page;
      await this.selectSubject(this.selectedSubject, true, false);
    },
    async selectSubject(subject, updateUrl = true, resetPage = true) {
      this.selectedSubject = subject;
      this.loading = true;

      if (resetPage) this.page = 1;

      // Update URL
      if (updateUrl) {
        this.$router.push({ query: { subject: subject.id } });
      }

      // Reuse the search API logic from ExplorePage but pre-filtered by subId
      var data = await this.$aesEncrypt({
        subId: subject.id,
        page: this.page,
        pageSize: this.pageSize,
      });

      this.$axios({
        url: "aitool/search", // Using search endpoint as it supports filtering by subId
        method: "POST",
        data: {
          data: data,
        },
      })
        .then((res) => {
          this.loading = false;
          window.scrollTo({ top: 0, behavior: "smooth" });
          if (res.code == 200) {
            this.total = res.data.total;
            this.subjectTools = res.data.list;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    clearSubject() {
      this.selectedSubject = null;
      this.$router.push({ query: {} });
    },
    formatNumber(num) {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + "k";
      }
      return num;
    },
  },
};
</script>
